import { FocusEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";

// Multi-lingual support
import { t } from "i18next";

// Services
import { getApi, postApi } from "../../../services/apiManager/ApiManager";

// Hooks & shared components
import { useToaster } from "../../../hooks/useToasterContext/useToasterContext";
import Loader from "../../layout/Loader";

// Components
import EudrStatusDescription from "./EudrStatusDescription";

// Types & validation
import {
  EMPTY_YIELD_CONFIG,
  IYieldConfigData,
  SUM_TOLERANCE,
  YIELD_MONTHS,
} from "./YieldConfigTypes";
import { getYieldConfigValidationSchema } from "./YieldConfigValidationSchema";

// API endpoints
const GET_ENDPOINT = "/v1/client/yield-config";
const UPDATE_ENDPOINT = "/v1/client/yield-config/update";

// Shared field styling: hide the native number-input spin buttons (up/down
// arrows) and slightly enlarge the field labels for readability.
const fieldSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  // Labels at a true 14px in both the resting and floated (shrink) states —
  // scale(1) on shrink stops MUI from scaling the floated label down.
  "& .MuiInputLabel-root": { fontSize: "14px" },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(12px, 7px) scale(1)",
  },
  // In disabled (view) mode MUI greys out the text heavily — darken it so
  // saved values stay clearly readable. ``WebkitTextFillColor`` is what
  // actually colours a disabled input's text.
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#272932",
    color: "#272932",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "rgba(39, 41, 50, 0.75)",
  },
  "& .MuiInputAdornment-root.Mui-disabled .MuiTypography-root": {
    color: "#272932",
  },
};

const YieldConfig = () => {
  const { setShowToaster } = useToaster();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Whether a configuration has actually been saved for this client. A saved
  // config always has a non-zero avg + months summing to 100, so an all-zero
  // payload means "not configured yet".
  const [isConfigured, setIsConfigured] = useState(false);
  const [loadedConfig, setLoadedConfig] =
    useState<IYieldConfigData>(EMPTY_YIELD_CONFIG);

  const validationSchema = useMemo(() => getYieldConfigValidationSchema(), []);

  const formik = useFormik<IYieldConfigData>({
    initialValues: loadedConfig,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        // Coerce all fields to numbers (TextField gives strings).
        const payload: IYieldConfigData = {
          ...values,
          yieldPerRaiPerYear: Number(values.yieldPerRaiPerYear) || 0,
          ...YIELD_MONTHS.reduce(
            (acc, m) => ({ ...acc, [m.field]: Number(values[m.field]) || 0 }),
            {},
          ),
        } as IYieldConfigData;

        const response = await postApi(UPDATE_ENDPOINT, payload);
        if (response.status) {
          setShowToaster(true, t("yieldConfig.saveSuccess"), "success");
          setIsEditing(false);
          await fetchConfig();
        } else {
          setShowToaster(
            true,
            t("commonMessages.internalServerError"),
            "error",
          );
        }
      } catch (error) {
        setShowToaster(true, t("commonMessages.internalServerError"), "error");
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Live running total of the 12 monthly percentages.
  const monthlyTotal = useMemo(
    () =>
      YIELD_MONTHS.reduce(
        (sum, m) => sum + (Number(formik.values[m.field]) || 0),
        0,
      ),
    [formik.values],
  );
  // The 12 monthly percentages must sum to 100.
  const isTotalValid = Math.abs(monthlyTotal - 100) <= SUM_TOLERANCE;
  const totalRounded = Math.round(monthlyTotal * 100) / 100;

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const response = await getApi(GET_ENDPOINT);
      if (response.status && response.data) {
        const data = response.data as IYieldConfigData;
        setLoadedConfig(data);
        const monthsSum = YIELD_MONTHS.reduce(
          (sum, m) => sum + (Number(data[m.field]) || 0),
          0,
        );
        setIsConfigured(Number(data.yieldPerRaiPerYear) > 0 || monthsSum > 0);
      }
    } catch (error) {
      setShowToaster(true, t("yieldConfig.loadError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    formik.resetForm({ values: loadedConfig });
    setIsEditing(false);
  };

  // Clear a lone default "0" on focus so the typed value replaces it (instead
  // of "023"). Selecting on focus is unreliable — a mouse click's mouseup
  // deselects it — so we clear the field value directly. Non-zero values are
  // selected so they can be overwritten too.
  const handleNumberFocus = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const input = e.target as HTMLInputElement;
    if (Number(input.value) === 0) {
      formik.setFieldValue(input.name, "");
    } else {
      input.select();
    }
  };

  // Restore 0 if the user leaves a field empty, so values stay numeric.
  // We mark the field touched WITHOUT re-validating the (stale) empty value,
  // then set 0 with validation — otherwise formik's blur validation runs
  // against "" first and flashes a false "Required" error on a 0 field.
  const handleNumberBlur = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const input = e.target as HTMLInputElement;
    if (input.value === "") {
      formik.setFieldTouched(input.name, true, false);
      formik.setFieldValue(input.name, 0, true);
    } else {
      formik.handleBlur(e);
    }
  };

  return (
    <Box>
      {isLoading && <Loader />}

      {/* Page title (outside the white card) */}
      <Box mt={2}>
        <Typography
          sx={{ color: "risinblack.main", fontWeight: 600, fontSize: 18 }}
        >
          {t("yieldConfig.title")}
        </Typography>
      </Box>

      {/* Full-size white card */}
      <Box className="farmerdata-content" sx={{ margin: "20px auto" }}>
        {/* Header bar */}
        <Box
          sx={{
            backgroundColor: "agricolors.drawerheader",
            display: "flex",
            alignItems: "center",
            pl: 2,
            pr: 3,
            height: 64,
            justifyContent: "space-between",
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        >
          <Typography
            sx={{ color: "risinblack.main", fontWeight: 600, fontSize: 18 }}
          >
            {t("yieldConfig.sectionTitle")}
          </Typography>
          {/* Edit lives at the card's top-right, near the content. */}
          {isConfigured && !isEditing && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleEdit}
              startIcon={<i className="icon-edit" />}
            >
              {t("yieldConfig.edit")}
            </Button>
          )}
        </Box>

        {/* Body — the shared `.farmerdata-mid-content` class provides the
            scroll (`height: calc(100vh - 320px); overflow-y: auto`), so the
            whole section (yield fields + EUDR description) scrolls together. */}
        <Box
          className="farmerdata-mid-content"
          width="100%"
          p={3}
          sx={{ backgroundColor: "farm.contrastText" }}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 3, whiteSpace: "nowrap" }}
          >
            {t("yieldConfig.subtitle")}
          </Typography>

          {!isEditing && !isConfigured ? (
            <Box>
              {/* Create button stays on the left */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleEdit}
                startIcon={<i className="icon-addnew" />}
              >
                {t("yieldConfig.create")}
              </Button>
              {/* Empty-state message centered in the page */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "40vh",
                }}
              >
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  {t("yieldConfig.notConfigured")}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              {/* Yield per rai per year — disabled in view mode, editable
                  only after Edit is clicked. */}
              <Box sx={{ maxWidth: 320, mb: 3 }}>
                <TextField
                  label={
                    <span>
                      {t("yieldConfig.avgYieldLabel")}
                      {isEditing && <sup className="sup-style">*</sup>}
                    </span>
                  }
                  id="yieldPerRaiPerYear"
                  name="yieldPerRaiPerYear"
                  type="number"
                  variant="filled"
                  fullWidth
                  disabled={!isEditing}
                  placeholder={t("yieldConfig.avgYieldPlaceholder")}
                  value={formik.values.yieldPerRaiPerYear}
                  onChange={formik.handleChange}
                  onBlur={handleNumberBlur}
                  onFocus={handleNumberFocus}
                  error={
                    formik.touched.yieldPerRaiPerYear &&
                    Boolean(formik.errors.yieldPerRaiPerYear)
                  }
                  helperText={
                    formik.touched.yieldPerRaiPerYear &&
                    formik.errors.yieldPerRaiPerYear
                  }
                  inputProps={{ min: 0, step: "any" }}
                  sx={fieldSx}
                />
              </Box>

              {/* Monthly distribution */}
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1.5 }}>
                {t("yieldConfig.monthlyDistribution")}
                {isEditing && <sup className="sup-style">*</sup>}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                  gap: 1.5,
                }}
              >
                {YIELD_MONTHS.map((month) => {
                  const monthLabel = t(`yieldConfig.months.${month.key}`);
                  const fieldError =
                    formik.touched[month.field] &&
                    Boolean(formik.errors[month.field]);
                  return (
                    <TextField
                      key={month.field}
                      label={monthLabel}
                      id={month.field}
                      name={month.field}
                      type="number"
                      variant="filled"
                      fullWidth
                      disabled={!isEditing}
                      value={formik.values[month.field]}
                      onChange={formik.handleChange}
                      onBlur={handleNumberBlur}
                      onFocus={handleNumberFocus}
                      error={fieldError}
                      helperText={fieldError ? formik.errors[month.field] : ""}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, max: 100, step: "any" }}
                      sx={fieldSx}
                    />
                  );
                })}
              </Box>

              {/* Running total */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mt: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("yieldConfig.total")}
                </Typography>
                <Chip
                  label={
                    isTotalValid
                      ? t("yieldConfig.totalValid", { total: totalRounded })
                      : `${totalRounded}%`
                  }
                  color={isTotalValid ? "success" : "error"}
                  variant={isTotalValid ? "filled" : "outlined"}
                  size="small"
                />
                {!isTotalValid && (
                  <Typography variant="caption" sx={{ color: "error.main" }}>
                    {t("yieldConfig.totalMustEqual")}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Description of EUDR Status — static, read-only bilingual content.
              Rendered inside the card body, the screen's only scroll region. */}
          <EudrStatusDescription />
        </Box>

        {/* Footer bar */}
        <Box
          sx={{
            backgroundColor: "agricolors.drawerheader",
            display: "flex",
            alignItems: "center",
            pl: 2,
            pr: 3,
            height: 64,
            justifyContent: "flex-end",
            gap: 2,
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          {isEditing && (
            <>
              <Button variant="outlined" onClick={handleCancel}>
                {t("yieldConfig.back")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => formik.handleSubmit()}
                disabled={!isTotalValid || !formik.isValid}
              >
                {t("yieldConfig.save")}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default YieldConfig;
